import { makeAutoObservable } from 'mobx';
import { DominosRootStore } from '.';
import { showInfoPopup } from '../../../modules/InfoPopup';
import cookies from '../../utils/storage/cookies';
import { goFish } from '../api';
import { getAppDefaultMessage } from '../dictionary';
import { isOrderStatus } from '../navigation';

/**
 * General assumption: page is reloaded after login or logout
 *
 * @export
 * @class UserStore
 */
export class UserStore {
    dominosStore: DominosRootStore;

    /**
     * Login state from back-end
     *
     * @memberof UserStore
     */
    isLogged = false;

    /**
     * User Id (cookie CurrentContact).
     * It is usefull only when user is logged.
     *
     * @type {string}
     * @memberof UserStore
     */
    userId: string;

    statusTimer: number;
    statusTimerDelay = 60000;

    /**
     * Favorite products
     *
     * @type {FavProduct[]}
     * @memberof UserStore
     */
    favs: FavProduct[] = [];

    favStorageName = 'favs';

    /**
     * Loading flag for favorites: product id
     *
     * @memberof UserStore
     */
    favsPending = '';

    constructor(store: DominosRootStore) {
        makeAutoObservable(this, {
            statusTimer: false,
            statusTimerDelay: false,
            userId: false,
            favStorageName: false
        });

        this.dominosStore = store;
    }

    /**
     * Setter for login status
     *
     * @memberof UserStore
     */
    private setUserState = (state: boolean) => {
        if (this.isLogged !== state) {
            if (state) {
                // Get actual Id
                this.userId = cookies.read('CurrentContact');
                // Get data from storage
                this.setFavourites(null);
            } else {
                this.userId = '';
            }
            this.isLogged = state;
        }
    };

    /**
     * Get actual user login status
     *
     * @memberof UserStore
     */
    checkStatus = async () => {
        if (!this.statusTimer) {
            // Start timer to kill to many calls
            this.statusTimer = window.setTimeout(() => {
                this.statusTimer = 0;
            }, this.statusTimerDelay);
            // Get status and update observable
            const statusResp = await goFish({ api: 'status' });
            try {
                if (statusResp.Status) {
                    if (statusResp.Logged) {
                        this.setUserState(true);
                    } else {
                        this.setUserState(false);
                    }
                }
            } catch (e) {
                this.setUserState(false);
            }
        }
    };

    updateFavourites = (favs: FavProduct[]): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (favs) {
                this.setFavourites(favs);
            }

            resolve();
        });
    };

    /**
     * Update observer and save data in storage
     *
     * @private
     * @memberof UserStore
     */
    private getStorageData = (): UserFavsStorageData => {
        let favsObject: UserFavsStorageData;
        // Get data saved before or create new data holder
        const favData = localStorage.getItem(this.favStorageName);
        try {
            favsObject = JSON.parse(favData);
        } finally {
            if (!favsObject) {
                favsObject = {};
            }
        }
        return favsObject;
    };

    /**
     * Update observer.
     * If favs is null data are grabbed from storage.
     * If favs is not null data in storage are updated.
     *
     * @private
     * @memberof UserStore
     */
    private setFavourites = (favs: FavProduct[] | null) => {
        // Get data saved before or create new data holder
        const favsObject = this.getStorageData();
        // At this point Id should always be something x is used for wierd cases when Id is empty
        const userId = this.userId || 'x';
        // Save updated data if favs was provided
        if (favs !== null) {
            favsObject[userId] = favs;
            localStorage.setItem(this.favStorageName, JSON.stringify(favsObject));
        }
        // Update observer
        this.favs = favsObject[userId] || [];
        this.favsPending = '';
    };

    private addFavourite = (guid: string) => {
        const favArr = this.favs.slice();
        favArr.push({ GUID: guid });
        this.setFavourites(favArr);
    };

    private removeFavourite = (guid: string) => {
        const is = this.isFav(guid);
        if (is >= 0) {
            const favArr = this.favs.slice();
            favArr.splice(is, 1);
            this.setFavourites(favArr);
        }
    };

    /**
     * Renew favs list if user is logged
     *
     * @memberof UserStore
     */
    getFavoriteList = () => {
        return new Promise((r) => {
            if (this.isLogged) {
                goFish({ api: 'favlist' }).then((favResp: FavoriteList) => {
                    if (favResp.Status && favResp.FavProduct) {
                        this.setFavourites(favResp.FavProduct);
                    }
                    r('');
                }, () => {
                    r('');
                });
            } else {
                r('');
            }
        });
    };

    /**
     * Like action
     * Use handleFavoriteAction if you want to like product
     *
     * @private
     * @memberof UserStore
     */
    private like = async (guid: string, name: string) => {
        try {
            const favResp: StandardResponse = await goFish({
                api: 'add',
                data: { productguid: guid }
            });
            if (favResp.Status) {
                window.InsiderQueue = [];
                window.InsiderQueue.push({
                    type: 'custom_event',
                    value: [
                        {
                            event_name: 'add_product_to_favorite_list',
                            event_parameters: {
                                custom: {
                                    product_name: name
                                }
                            }
                        }
                    ]
                });
                window.Insider && window.Insider.eventManager.dispatch('init-manager:re-initialize');
                this.addFavourite(guid);
            } else {
                throw favResp;
            }
        } catch (e) {
            showInfoPopup({ text: e.Message || getAppDefaultMessage('errors.default') });
            this.favsPending = '';
        }
    };

    /**
     * Dislake action
     * Use handleFavoriteAction if you want dislike product
     *
     * @private
     * @memberof UserStore
     */
    private dislike = async (guid: string) => {
        try {
            const favResp: StandardResponse = await goFish({
                api: 'del',
                data: { productguid: guid }
            });
            if (favResp.Status) {
                this.removeFavourite(guid);
            } else {
                throw favResp;
            }
        } catch (e) {
            showInfoPopup({ text: e.Message || getAppDefaultMessage('errors.default') });
            this.favsPending = '';
        }
    };

    /**
     * Returns index of liked product or -1 if it's not liked
     *
     * @memberof UserStore
     */
    isFav = (guid: string): number => {
        let index = -1;
        this.favs.some((product, i) => {
            if (guid === product.GUID) {
                index = i;
                return true;
            }
        });
        return index;
    };

    /**
     * Is product liked
     *
     * @memberof UserStore
     */
    isProductFavourite = (guid: string): boolean => {
        const index = this.isFav(guid);
        return index < 0 ? false : true;
    };

    /**
     * Sets like/dislike on product.
     * Uses store to detect if its liked already or not.
     *
     * @param {string} guid
     * @param {string} name
     * @memberof UserStore
     */
    handleFavoriteAction = (guid: string, name: string) => {
        if (!this.favsPending) {
            this.favsPending = guid;
            const p = this.isProductFavourite(guid);
            if (p) {
                this.dislike(guid);
            } else {
                this.like(guid, name);
            }
        }
    };

    /**
     * Details of order specified by guid
     *
     * @memberof UserStore
     */
    getOrderDetails = (orderGuid: string) => {
        return goFish({
            api: 'getuserorder',
            data: { orderGuid }
        }).then(
            (response: OrderCartResponse) => {
                return response;
            },
            err => {
                return err as OrderCartResponse;
            }
        );
    };

    /**
     * Orders list
     *
     * @memberof UserStore
     */
    getLastOrders = async (): Promise<LastOrder[]> => {
        try {
            const orders: LastOrdersResponse = await goFish({ api: 'getuserorders' });
            return orders.LastOrders || [];
        } catch (e) {
            return [];
        }
    };
}
